import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRODUCTS, WHATSAPP_LINK } from "../../lib/constants";
import { MessageCircle, X, Send, ChevronRight, ShoppingBag } from "lucide-react";

const WhatsAppChatbot = ({ cartItems, cartTotal, onClearCart }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "¡Hola! 🍫 Soy el asistente de Urban Brownies. ¿Qué brownie quieres probar hoy?",
    },
  ]);
  const [currentStep, setCurrentStep] = useState("welcome");
  const [selectedItems, setSelectedItems] = useState([]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const quickOptions = {
    welcome: [
      { id: "ver-menu", text: "Ver menú", icon: "🍫" },
      { id: "ofertas", text: "Ofertas del día", icon: "🎁" },
      { id: "mi-pedido", text: "Mi pedido", icon: "🛒" },
      { id: "hablar", text: "Hablar con humano", icon: "👋" },
    ],
    menu: PRODUCTS.slice(0, 4).map((p) => ({
      id: p.id,
      text: `${p.name} - ${p.priceRange}`,
      icon: "🍫",
    })),
  };

  const handleOptionClick = (option) => {
    // Add user message
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), type: "user", text: option.text },
    ]);

    // Bot response based on option
    setTimeout(() => {
      let botResponse = "";
      let nextStep = currentStep;

      switch (option.id) {
        case "ver-menu":
          botResponse = "¡Excelente elección! Estos son nuestros productos más populares:";
          nextStep = "menu";
          break;
        case "ofertas":
          botResponse = "🔥 ¡Hoy tenemos promo! Pide antes de medianoche y recibe un brownie misterioso GRATIS. ¿Te interesa?";
          break;
        case "mi-pedido":
          if (cartItems.length > 0) {
            botResponse = `Tienes ${cartItems.length} producto(s) en tu carrito por ${formatPrice(cartTotal)}. ¿Quieres completar tu pedido?`;
          } else {
            botResponse = "Tu carrito está vacío. ¿Qué te gustaría pedir?";
          }
          break;
        case "hablar":
          botResponse = "¡Perfecto! Te conecto con nuestro equipo ahora mismo...";
          setTimeout(() => {
            window.open(WHATSAPP_LINK, "_blank");
          }, 1000);
          break;
        default:
          // Product selected
          const product = PRODUCTS.find((p) => p.id === option.id);
          if (product) {
            botResponse = `¡Buena elección! ${product.name} es uno de nuestros favoritos. ${product.description}\n\n¿Lo agregamos a tu pedido?`;
            setSelectedItems([product]);
          }
          break;
      }

      setMessages((prev) => [
        ...prev,
        { id: Date.now(), type: "bot", text: botResponse },
      ]);
      setCurrentStep(nextStep);
    }, 500);
  };

  const handleSendToWhatsApp = () => {
    let message = "¡Hola Urban Brownies! 🍫\n\n";

    if (cartItems.length > 0) {
      message += "Quiero hacer el siguiente pedido:\n\n";
      cartItems.forEach((item, index) => {
        message += `${index + 1}. ${item.name}\n`;
      });
      message += `\nTotal: ${formatPrice(cartTotal)}`;
    } else if (selectedItems.length > 0) {
      message += `Me interesa: ${selectedItems.map((i) => i.name).join(", ")}`;
    } else {
      message += "Quiero hacer un pedido";
    }

    window.open(`${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-5 z-[999] bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20BD5A] transition-colors animate-pulse-glow"
        data-testid="whatsapp-chatbot-btn"
      >
        <MessageCircle className="w-7 h-7" strokeWidth={1.5} />
        {cartItems.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#C88037] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {cartItems.length}
          </span>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] bg-white rounded-2xl shadow-2xl overflow-hidden z-50 chatbot-bubble"
            data-testid="whatsapp-chatbot-window"
          >
            {/* Header */}
            <div className="bg-[#25D366] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <span className="text-xl">🍫</span>
                </div>
                <div>
                  <h4 className="font-semibold">Urban Brownies</h4>
                  <p className="text-xs text-white/80">En línea • Responde al instante</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                data-testid="close-chatbot"
              >
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>

            {/* Messages */}
            <div className="h-64 overflow-y-auto p-4 space-y-3 bg-[#ECE5DD]">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-xl text-sm ${
                      msg.type === "user"
                        ? "bg-[#DCF8C6] text-[#1A1A1A] rounded-br-sm"
                        : "bg-white text-[#1A1A1A] rounded-bl-sm shadow-sm"
                    }`}
                  >
                    {msg.text}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Quick Options */}
            <div className="p-4 border-t border-[#E5DCD0] bg-white">
              <div className="flex flex-wrap gap-2 mb-3">
                {(currentStep === "menu" ? quickOptions.menu : quickOptions.welcome).map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleOptionClick(option)}
                    className="flex items-center gap-1 bg-[#F4EDE4] text-[#2D1810] px-3 py-2 rounded-full text-sm hover:bg-[#E5DCD0] transition-colors"
                    data-testid={`chatbot-option-${option.id}`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.text}</span>
                  </button>
                ))}
              </div>

              {/* Cart Summary */}
              {cartItems.length > 0 && (
                <div className="bg-[#F4EDE4] rounded-xl p-3 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-[#2D1810]">
                      <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                      <span>{cartItems.length} productos</span>
                    </div>
                    <span className="font-bold text-[#C88037]">{formatPrice(cartTotal)}</span>
                  </div>
                </div>
              )}

              {/* Send to WhatsApp Button */}
              <button
                onClick={handleSendToWhatsApp}
                className="w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors"
                data-testid="chatbot-send-whatsapp"
              >
                <Send className="w-5 h-5" strokeWidth={1.5} />
                Continuar en WhatsApp
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppChatbot;
